import { useEffect, useId, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { INTERCOM_TOUR_STEPS } from "@/helpers/constants";
import type { LocationType } from "@/helpers/location";
import { cn } from "@/lib/utils";

import Predictions from "./predictions";

interface LocationInputProps {
  id: string;
  location: LocationType;
  onLocation(newLocation: LocationType): void;
  onProgress(): void;
  size?: "small";
}

export default function LocationInput({
  id,
  location,
  onLocation,
  onProgress,
  size,
}: LocationInputProps) {
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleInputFocus = () => setIsOpen(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when location changes
  useEffect(() => {
    if (location && !query) {
      setQuery(location.address || "");
    }
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const isSmall = size === "small";

  return (
    <div ref={containerRef}>
      <Input
        aria-autocomplete="none"
        aria-controls={menuId}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        autoComplete="off"
        autoCorrect="off"
        className={cn(
          INTERCOM_TOUR_STEPS.locationInput,
          "mt-[3px] h-6 rounded-none border-0 p-0 text-16 text-black placeholder:text-black",
          isSmall && "md:mt-0 md:h-5.5"
        )}
        id={id}
        onChange={handleInputChange}
        onClick={handleClick}
        onFocus={handleInputFocus}
        placeholder="Finding your location…"
        spellCheck="false"
        type="search"
        value={query}
      />
      {isOpen && (
        <Predictions
          className={cn(!isSmall && "md:min-w-100")}
          id={menuId}
          onClose={() => setIsOpen(false)}
          onLocation={onLocation}
          onProgress={onProgress}
          onQuery={setQuery}
          query={query.trim()}
        />
      )}
    </div>
  );
}
